import React, {
  useState,
  useRef,
  useEffect,
  FormEvent,
  CompositionEvent,
  ClipboardEvent,
} from 'react';
import styled from 'styled-components';
import CustomText from '../text/CustomText';

interface CustomEditorProps {
  value: string;
  onChange?: (value: string) => void;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}

const CustomEditor: React.FC<CustomEditorProps> = ({
  value,
  onChange,
  children,
  className,
  disabled = false,
  placeholder,
  maxLength,
}) => {
  const [isComposing, setIsComposing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [content, setContent] = useState(value);
  const [isEditable, setIsEditable] = useState(false);
  const editableRef = useRef<HTMLDivElement>(null);

  // 외부 value → 내부 state
  useEffect(() => {
    setContent(value);
  }, [value]);

  // 내부 state → DOM 반영
  useEffect(() => {
    if (editableRef.current && editableRef.current.innerText !== content) {
      editableRef.current.innerText = content;
    }
  }, [content]);

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (e: CompositionEvent<HTMLDivElement>) => {
    setIsComposing(false);
    const newValue = e.currentTarget.innerText;
    setContent(newValue);
    onChange?.(newValue);
  };

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    if (!isComposing) {
      const newValue = e.currentTarget.innerText;
      setContent(newValue);
      onChange?.(newValue);
    }
  };

  /**
   * "실제로 텍스트가 DOM에 들어가기 직전"에 호출
   * - 글자 수가 이미 초과되어 있다면 preventDefault()로 막음
   */
  const handleBeforeInput = (e: any) => {
    if (!disabled && maxLength) {
      // e.data: 이번에 추가될 문자
      // composition 중이면 e.data가 null일 수 있으니 체크
      const incomingText = e.data ?? '';
      const currentText = editableRef.current?.innerText ?? '';

      // selection(드래그 영역) 삭제 후 새 문자 입력하는 경우 고려해서
      // 새로 입력될 전체 길이를 미리 계산
      const selection = window.getSelection();
      let nextLength = currentText.length;
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString();
        // 드래그된 텍스트를 지우고 새 문자를 입력하는 길이를 계산
        nextLength = currentText.length - selectedText.length + incomingText.length;
      }

      if (nextLength > maxLength) {
        e.preventDefault(); // 초과 입력 막기
        setIsEditable(false);
        setIsComposing(false);
        document.execCommand('insertText', false, '');
      }
    }
  };

  /**
   * 붙여넣기 시에도 글자수가 초과되면 막거나, 초과한 부분 잘라내기
   */
  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (!disabled && maxLength) {
      const pasteData = e.clipboardData.getData('text');
      const currentText = editableRef.current?.innerText ?? '';
      // selection(드래그 영역) 삭제 후 새 문자열 붙여넣는 경우 고려
      const selection = window.getSelection();
      let nextLength = currentText.length;
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString();
        nextLength = currentText.length - selectedText.length + pasteData.length;
      }

      // 붙여넣기 후 길이가 초과한다면 초과분 잘라내기
      if (nextLength > maxLength) {
        e.preventDefault();
        const allowedText = pasteData.slice(
          0,
          maxLength - (currentText.length - (selection?.toString().length ?? 0)),
        );
        // 직접 넣어주기
        document.execCommand('insertText', false, allowedText);
      }
    }
  };

  const handleContainerClick = () => {
    if (!disabled) {
      setIsEditable(true);
      editableRef.current?.focus();
    }
  };

  return (
    <StyledCustomEditor className={className} onClick={handleContainerClick}>
      <div
        className="customEditor__input"
        ref={editableRef}
        contentEditable={!disabled && isEditable}
        suppressContentEditableWarning
        onCompositionStart={disabled ? undefined : handleCompositionStart}
        onCompositionEnd={disabled ? undefined : handleCompositionEnd}
        onBeforeInput={disabled ? undefined : handleBeforeInput} // 추가
        onInput={disabled ? undefined : handleInput}
        onPaste={disabled ? undefined : handlePaste} // 추가
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      {/* placeholder 처리 */}
      {!content && !!placeholder && !isFocused && !children && (
        <div className="customEditor__placeholer">
          <CustomText fontSize={18} fontWeight={400} fontColor="#B2B6B8">
            {placeholder}
          </CustomText>
        </div>
      )}
      {children && <div contentEditable={false}>{children}</div>}
    </StyledCustomEditor>
  );
};

const StyledCustomEditor = styled.div`
  min-height: 100px;
  width: 100%;
  border: 1px solid #ccc;
  padding: 8px;
  outline: none;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  position: relative;

  .customEditor__input {
    &:focus-visible {
      outline: none;
    }
  }
  .customEditor__placeholer {
    position: absolute;
    top: 16px;
    left: 16px;
    pointer-events: none;
  }
`;

export default CustomEditor;
